import React from "react"
import { useForm, FormProvider } from "react-hook-form"
import { Group, Modal, Stack, Text, useMantineTheme } from "@mantine/core"
import { useStyles } from "@/pages/campaign-form/components/donation-form/payroll-deduction-form-modal/PayrollFormModal.styles"
import { CancelPayrollForm } from "@/pages/campaign-form/components/donation-form/payroll-deduction-form-modal/CancelPayrollForm"
import { AddPayrollForm } from "@/pages/campaign-form/components/donation-form/payroll-deduction-form-modal/AddPayrollForm"
import { Radio } from "@/components/Controlled/Controlled"
import {
    IFormModalData,
    INotification,
    IPayrollFormModalProps,
} from "@/pages/campaign-form/components/donation-form/payroll-deduction-form-modal/types"
import { notifications } from "@mantine/notifications"
import { Button, Icon } from "@/components"
import axios from "axios"

const { SupporterActionType } = DjangIO.app.grassroots.enums
const { PayrollDonationType } = DjangIO.app.grassroots.types

declare global {
    interface Window {
        dangerouslyTriggerResourceTableRefresh: () => void
    }
}

export const PayrollFormModal = (props: IPayrollFormModalProps) => {
    const { togglePayrollModal, isModalOpen } = props

    const { classes } = useStyles()
    const theme = useMantineTheme()
    const reactHookForm = useForm({
        defaultValues: {
            payroll_deduction_option: SupporterActionType.authorized_contribution.value,
            payroll_deduction_type: PayrollDonationType.amount.value,
            contact: "",
        },
    })

    const payrollDeductionOption: number = reactHookForm.watch("payroll_deduction_option")
    const payrollDeductionType: number = reactHookForm.watch("payroll_deduction_type")

    const { isSubmitting } = reactHookForm.formState

    const handleToggleModal = () => {
        reactHookForm.reset()
        togglePayrollModal()
    }

    const showNotification = ({ text, title, iconColor, bgColor, icon }: INotification) => {
        const breakLine = /\\n\\n*|\\n/g
        notifications.show({
            autoClose: false,
            title: title,
            message: (
                <Stack align="flex-start" spacing="md">
                    <Text text-align="center">{text.replace(breakLine, ". ")}</Text>
                </Stack>
            ),
            color: bgColor,
            icon: <Icon icon={icon} color={iconColor} />,
            closeButtonProps: { "aria-label": "Dismiss notification" },
        })
    }

    const extractErrorMessage = (message: string) => {
        const match = message.match(/\[u'(.*?)'\]/)

        if (match && match.length > 1) {
            return match[1]
        } else {
            return null
        }
    }

    const postPayrollDeduction = (payload) => {
        const isCancelOption =
            payload.payroll_frequency_type === undefined && payload.payroll_donation_type === undefined
        return axios
            .post("/api/supporteraction/authorized_contribution/", payload)
            .then(() => {
                if (isCancelOption) {
                    showNotification({
                        text: "Payroll canceled successfully",
                        title: "Canceled payroll",
                        iconColor: theme.colors.green[5],
                        bgColor: "green.0",
                        icon: "check",
                    })
                } else {
                    showNotification({
                        text: "Payroll created successfully",
                        title: "Created payroll",
                        iconColor: theme.colors.green[5],
                        bgColor: "green.0",
                        icon: "check",
                    })
                }
                handleToggleModal()
            })
            .catch((error) => {
                const errorMessage = extractErrorMessage(error.response.data.error)
                if (isCancelOption) {
                    showNotification({
                        title: "Failed to cancel your payroll",
                        text: error.response.data.error ? error.response.data.error : "Failed to cancel payroll",
                        iconColor: theme.colors.red[1],
                        bgColor: "red.0",
                        icon: "times",
                    })
                } else {
                    showNotification({
                        title: "Failed to create your payroll",
                        text: errorMessage ? errorMessage : "Failed to create payroll",
                        iconColor: theme.colors.red[1],
                        bgColor: "red.0",
                        icon: "times",
                    })
                }
            })
            .finally(() => window.dangerouslyTriggerResourceTableRefresh())
    }

    const cancelPayroll = (data: IFormModalData) => {
        const cancelData = {
            supporter: DjangIO.app.grassroots.models.Supporter.resourceUriFromId(data.contact),
        }
        return postPayrollDeduction(cancelData)
    }

    const createNewPayroll = (data: IFormModalData) => {
        switch (payrollDeductionType) {
            case PayrollDonationType.amount.value:
                const amountData = {
                    supporter: data.contact,
                    payroll_frequency_type: Number(data.payroll_frequency),
                    payroll_donation_type: data.payroll_deduction_type,
                    amount: (Number(data.payroll_amount) * 100).toString(), // cents
                    action_center: data.action_center,
                }
                return postPayrollDeduction(amountData)

            case PayrollDonationType.percentage.value:
                const percentageData = {
                    action_center: data.action_center,
                    supporter: data.contact,
                    payroll_frequency_type: Number(data.payroll_frequency),
                    payroll_donation_type: data.payroll_deduction_type,
                    percentage_label: data.percentage_text,
                    payroll_decimal_value: data.percentage_value,
                }
                return postPayrollDeduction(percentageData)
        }
    }

    const onSubmitForm: any = (data: IFormModalData) => {
        if (data.payroll_deduction_option === SupporterActionType.authorized_contribution.value) {
            return createNewPayroll(data)
        } else {
            return cancelPayroll(data)
        }
    }

    return (
        <Modal.Root
            opened={isModalOpen}
            onClose={handleToggleModal}
            centered
            size={"80%"}
            closeOnClickOutside={true}
            className={classes.modalContainer}
        >
            <Modal.Overlay />
            <FormProvider {...reactHookForm}>
                <form aria-labelledby="form-title" onSubmit={reactHookForm.handleSubmit(onSubmitForm)}>
                    <Modal.Content className={classes.modalContent}>
                        <Modal.Header className={classes.modalHeader}>
                            <Modal.Title className={classes.modalTitle}>Manage Payroll Deduction</Modal.Title>
                            <Modal.CloseButton className={classes.modalCloseButton} aria-label="Cancel" size="md" />
                        </Modal.Header>
                        <Modal.Body className={classes.modalBody}>
                            <div className={classes.content}>
                                <Radio.Group
                                    name="payroll_deduction_option"
                                    label="Select an option below"
                                    transform={{
                                        input: (input: string | number) => String(input),
                                        output: (output: string) => Number(output),
                                    }}
                                    onChange={() => reactHookForm.setValue("contact", "")}
                                >
                                    <Group>
                                        <Radio
                                            value={SupporterActionType.authorized_contribution.value.toString()}
                                            label="Add a new payroll deduction"
                                        />
                                        <Radio value="0" label="Cancel an existing payroll deduction" />
                                    </Group>
                                </Radio.Group>
                                {payrollDeductionOption === SupporterActionType.authorized_contribution.value ? (
                                    <AddPayrollForm />
                                ) : (
                                    <CancelPayrollForm />
                                )}
                            </div>
                        </Modal.Body>
                        <Group className={classes.modalButtons}>
                            <Button onClick={handleToggleModal} variant="outline" className={classes.cancelButton}>
                                Cancel
                            </Button>
                            <Button
                                data-cy="submit_payroll"
                                loading={isSubmitting}
                                type="submit"
                                className={classes.submitButton}
                            >
                                Submit
                            </Button>
                        </Group>
                    </Modal.Content>
                </form>
            </FormProvider>
        </Modal.Root>
    )
}
